import { AllStyledComponent } from "@remirror/styles/emotion";
import {
  Remirror,
  ThemeProvider,
  useRemirror,
  useCommands,
  useRemirrorContext,
} from "@remirror/react";
import { connect } from "react-redux";
import { BoldExtension } from '@remirror/extension-bold';
import { ItalicExtension } from '@remirror/extension-italic';
import { StrikeExtension } from '@remirror/extension-strike';
import { HeadingExtension } from '@remirror/extension-heading';
import { CustomLinkExtension, CustomLinkMarkExtension } from './components/CustomLinkExtension';
import { PlaceholderExtension } from '@remirror/extension-placeholder';
import { MarkdownExtension } from '@remirror/extension-markdown';
import { DocExtension } from '@remirror/extension-doc';
import { CodeBlockExtension } from '@remirror/extension-code-block';
import React, { useState, useRef, useEffect, useMemo, createContext, useContext, useCallback } from "react";
// import { createContextState } from "create-context-state";
import { useFileUploadProgress } from "../../../providers/FileUploader";
import { useField } from "formik";
import { useFormikFieldValidation } from "../../../hooks/useFormikFieldValidation";
import {
  FieldContainer,
  Field,
  Label,
  HelperLine,
  HelperText,
  CharacterCounter,
  ErrorMessage,
} from "../Field";
import { RichTextEditorStyles, CharacterCounterWrapper } from "./styles";
import { CustomVideoExtensionStyles } from "./components/RichVideoExtension/styles";
import { CustomImageExtensionStyles } from "./components/RichImageExtension/styles";
import { CustomVideoExtension } from './components/RichVideoExtension'
import { CustomPdfExtension } from './components/RichPdfExtension'
import { CustomImageExtension } from './components/RichImageExtension'
import { CustomAudioExtension } from "./components/RichAudioExtension";
import { RichInstagramReelsExtension } from "./components/RichInstagramReelsExtension";
import VisualToolBar from "./components/VisualToolBar";
import { User } from "../../../types";
import LinkPopover from "./components/LinkPopover.tsx";
import InsertLinkModal from "./components/InsertLinkModal";
import {
  getMediaBreakdown
} from "../../../helpers/formBodyCharacterLimitValidation";
import { ParagraphExtension } from "@remirror/extension-paragraph";
import Snackbar from "../../Snackbar";
import { Extension } from '@remirror/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { CustomAudioExtensionStyles } from "./components/RichAudioExtension/styles";
import { CustomPdfExtensionStyles } from "./components/RichPdfExtension/styles";
import { CustomMediaExtensionStyles } from "./components/MediaUploadedPlaceholder/styles";

// Remove the complex SafeUndoExtension and use onChange error handling instead


const createExtensions = (
  mediaFilesMap?: Record<number, any>,
  onEditLink?: (text: string, href: string) => void,
  blockFilesDownload: boolean = true,
  placeholder?: string
) => [
    new ParagraphExtension({}),
    new MarkdownExtension({}),
    new BoldExtension({}),
    new ItalicExtension(),
    new StrikeExtension(),
    new HeadingExtension({ levels: [1, 2, 3, 4, 5, 6] }),
    new CustomLinkMarkExtension(),
    (() => {
      const customLinkExt = new CustomLinkExtension({ autoLink: true, selectTextOnClick: true });
      if (onEditLink) {
        customLinkExt.setEditLinkCallback(onEditLink);
      }
      return customLinkExt;
    })(),
    new PlaceholderExtension({ placeholder: placeholder || 'Desarrolla el artículo aquí...' }),
    new CustomImageExtension(mediaFilesMap || {}, false, blockFilesDownload),
    new CustomPdfExtension(mediaFilesMap || {}, false, blockFilesDownload),
    new CustomVideoExtension(mediaFilesMap || {}, false, blockFilesDownload),
    new CustomAudioExtension(mediaFilesMap || {}, false, blockFilesDownload),
    new RichInstagramReelsExtension()
  ];

interface Props {
  visual: ReturnType<typeof useRemirror>;
  markdown: ReturnType<typeof useRemirror>;
}

interface Context extends Props {
  setMarkdown: (markdown: string) => void;
  setVisual: (markdown: string) => void;
}

// DUAL EDITOR PROVIDER
const DualEditorContext = createContext<Context | undefined>(undefined);

const DualEditorProvider: React.FC<Props & { children: React.ReactNode }> = ({ visual, markdown, children }) => {
  const contextValue: Context = {
    visual,
    markdown,
    setMarkdown: (markdownStr: string) => {
      return markdown.getContext()?.setContent({
        type: "doc",
        content: [
          {
            type: "codeBlock",
            attrs: { language: "markdown" },
            content: markdownStr ? [{ type: "text", text: markdownStr }] : undefined,
          },
        ],
      });
    },
    setVisual: (markdownStr: string) => {
      return visual.getContext()?.setContent(markdownStr);
    },
  };

  return (
    <DualEditorContext.Provider value={contextValue}>
      {children}
    </DualEditorContext.Provider>
  );
};

const useDualEditor = () => {
  const context = useContext(DualEditorContext);
  if (context === undefined) {
    throw new Error('useDualEditor must be used within a DualEditorProvider');
  }
  return context;
};

// Función para contar archivos existentes en el contenido
const countExistingMediaFiles = (jsonContent: any): number => {
  if (!jsonContent?.content) return 0;

  let count = 0;
  const traverse = (node: any) => {
    if (node.type === 'image' || node.type === 'customVideo' || node.type === 'customPdf' || node.type === 'customAudio' || node.type === "instagramReels") {
      count++;
    }
    if (node.content) {
      node.content.forEach(traverse);
    }
  };

  jsonContent.content.forEach(traverse);
  return count;
};

// Function to get pure text content excluding media nodes
const getPureTextFromJSON = (jsonContent: any): string => {
  if (!jsonContent?.content) return '';

  let text = '';
  const traverse = (node: any) => {
    // Skip media nodes entirely - don't count them as characters
    if (node.type === 'image' || node.type === 'customVideo' || node.type === 'customPdf' || node.type === 'customAudio' || node.type === "instagramReels") {
      return;
    }

    // Extract text from text nodes
    if (node.type === 'text') {
      text += node.text || '';
    }

    // Add line breaks for certain block elements (but we'll remove them later)
    if (node.type === 'paragraph' || node.type === 'heading') {
      if (node.content) {
        node.content.forEach(traverse);
      }
      // Don't add line breaks here since we'll remove them anyway
    } else if (node.content) {
      node.content.forEach(traverse);
    }
  };

  jsonContent.content.forEach(traverse);
  return text;
};

interface VisualEditorProps {
  onChange?;
  onBlur?;
  onFocus?;
  showToolbar?: boolean;
  disabled?: boolean;
  name?: string;
  entityType?: string;
  userId?: string | number;
  onUploadCountChange?: (count: number) => void;
  max?: number;
  customToolbarItems?: string[];
  mediaFilesMap?: Record<number, any>;
  maxFiles?: number;
  blockFilesDownload?: boolean;
}

const VisualEditor = ({
  onChange,
  onBlur,
  onFocus,
  showToolbar = true,
  disabled,
  name,
  entityType,
  userId,
  onUploadCountChange,
  max,
  customToolbarItems = [],
  maxFiles = 5,
  blockFilesDownload = true
}: VisualEditorProps) => {
  const { visual, setMarkdown } = useDualEditor();
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"error" | "success" | "info">("error");
  const { uploadFileSimple } = useFileUploadProgress();
  const [isEditable, setIsEditable] = useState(true);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [currentFileCount, setCurrentFileCount] = useState(0);

  // función wrapper que mantiene el contador de archivos subiendose
  const uploadWithContext = useCallback(async (file, onProgress) => {
    // Verificar límite antes de subir
    if (currentFileCount + uploadingCount >= maxFiles) {
      setSnackbarMessage(`No puedes subir más de ${maxFiles} archivos en total.`);
      setSnackbarType("error");
      setSnackbarVisible(true);
      throw new Error(`UPLOAD_LIMIT_EXCEEDED: Límite de ${maxFiles} archivos alcanzado`);
    }

    setUploadingCount(c => {
      const next = c + 1;
      onUploadCountChange?.(next);
      return next;
    });
    try {
      return await uploadFileSimple(file, `${name}_attached_files`, userId, entityType, onProgress);
    } finally {
      setUploadingCount(c => {
        const next = c - 1;
        onUploadCountChange?.(next);
        return next;
      });
    }
  }, [uploadFileSimple, name, userId, entityType, onUploadCountChange, currentFileCount, uploadingCount, maxFiles]);

  useEffect(() => {
    const handleDrop = (event: DragEvent) => {
      if (!event.dataTransfer?.files?.length) return;
      event.preventDefault();
      event.stopPropagation();
      setSnackbarMessage("Debes adjuntar los archivos directo desde las herramientas del editor.");
      setSnackbarType("error");
      setSnackbarVisible(true);
    };

    const handlePaste = (event: ClipboardEvent) => {
      const clipboardData = event.clipboardData;
      if (!clipboardData) return;

      // Check for files
      const hasFiles = clipboardData.files && clipboardData.files.length > 0;

      // Check for image data (from paper notes, etc.)
      const hasImageData = clipboardData.types.some(type =>
        type.startsWith('image/') ||
        type === 'image/png' ||
        type === 'image/jpeg' ||
        type === 'image/gif' ||
        type === 'image/webp'
      );

      // Check for video and audio data
      const hasVideoAudioData = clipboardData.types.some(type =>
        type.startsWith('video/') ||
        type.startsWith('audio/') ||
        type === 'video/mp4' ||
        type === 'video/webm' ||
        type === 'video/quicktime' ||
        type === 'audio/mp3' ||
        type === 'audio/wav'
      );

      // Check for HTML content that might contain videos or audio (from webpages)
      const hasHtmlWithMedia = clipboardData.types.includes('text/html') &&
        (clipboardData.getData('text/html')?.includes('<video') ||
          clipboardData.getData('text/html')?.includes('<audio') ||
          clipboardData.getData('text/html')?.includes('<img'));

      if (hasFiles || hasImageData || hasVideoAudioData || hasHtmlWithMedia) {
        event.preventDefault();
        event.stopPropagation();
        setSnackbarMessage("Solo se permite copiar y pegar texto. Para agregar archivos usa la barra de herramientas del editor.");
        setSnackbarType("error");
        setSnackbarVisible(true);
      }
    };

    const preventDefault = (event: Event) => {
      event.preventDefault();
    };

    // Bloquear eventos inesperados globales
    window.addEventListener("drop", handleDrop, { capture: true });
    window.addEventListener("paste", handlePaste as EventListener, { capture: true });
    window.addEventListener("dragover", preventDefault, { capture: true }); // 👈 evita abrir archivos arrastrados fuera
    window.addEventListener("dragenter", preventDefault, { capture: true });

    return () => {
      window.removeEventListener("drop", handleDrop, { capture: true });
      window.removeEventListener("paste", handlePaste as EventListener, { capture: true });
      window.removeEventListener("dragover", preventDefault, { capture: true });
      window.removeEventListener("dragenter", preventDefault, { capture: true });
    };
  }, []);



  return (
    <>
      <Remirror
        manager={visual.manager}
        initialContent={visual.state}
        autoRender="end"
        editable={!disabled}
        onChange={({ helpers, state }) => {
          try {
            const jsonContent = helpers.getJSON();

            // Get pure text without media nodes and remove line breaks
            const pureText = getPureTextFromJSON(jsonContent);
            const textWithoutLineBreaks = pureText.replace(/\n|\r\n|\r/g, '');
            const textCount = textWithoutLineBreaks.length;

            const fullText = helpers.getText(); // Keep for word count and other purposes
            const mediaBreakdown = getMediaBreakdown(jsonContent);
            const wordsCount = fullText.trim().split(/\s+/).filter(Boolean).length;
            const isCountValid = typeof max === 'number' ? textCount <= max : true;

            // Actualizar contador de archivos existentes
            const existingFileCount = countExistingMediaFiles(jsonContent);
            setCurrentFileCount(existingFileCount);

            // Verificar si se excede el límite de archivos
            const isFileCountValid = existingFileCount <= maxFiles;

            if (onChange) {
              onChange({
                markdownStr: helpers.getMarkdown(state),
                json: jsonContent,
                textStr: fullText,
                htmlStr: helpers.getHTML(state),
                characterCount: textCount,
                textCharacterCount: textCount,
                mediaBreakdown,
                wordsCount,
                isCountValid,
                uploadingCount,
                fileCount: existingFileCount,
                isFileCountValid
              });
            }

            setIsEditable(true);
            setMarkdown(helpers.getMarkdown(state));
          } catch (error) {
            // Prevent invalid state from being processed
            return;
          }
        }}
        onBlur={(params, event) => {
          if (event && name) {
            const syntheticEvent = {
              ...event,
              target: {
                ...event.target,
                name, // ⬅️ le inyectás el name
              },
            };
            onBlur?.(syntheticEvent);
          } else {
            onBlur?.(event);
          }
        }}
        onFocus={(params, event) => onFocus?.(event)}
      >
        <VisualToolBar
          uploadSingleFileWithContext={uploadWithContext}
          showToolbar={showToolbar}
          customToolbarItems={customToolbarItems}
        // disabled={disabled}
        />
      </Remirror>
      <Snackbar
        visible={snackbarVisible}
        options={{ type: snackbarType }}
        onHide={() => setSnackbarVisible(false)}
      >
        {snackbarMessage}
      </Snackbar>
    </>
  );
};


// COMPONENT PRINCIPAL
interface TextareaProps {
  name: string;
  value?: string | number;
  disabled?: boolean;
  showToolbar?: boolean;
  error?: string;
  touched?: {};
  onFocus?: (data: any) => void;
  onBlur?: (data: any) => void;
  placeholder?: string;
  entityType: "session" | "post" | "group" | "welcome_content" | "session_reminder";
  user: User;
  onChange?: (data: any) => void;
  onUploadCountChange?: (count: number) => void;
  customToolbarItems?: string[];
  max?: number;
  maxFiles?: number;
  mediaFilesMap?: Record<number, any>;
  blockFilesDownload?: boolean;
  options: {
    height?: number;
    label?: string | React.ReactNode;
    skin?: "base";
    size?: "md" | "lg";
    helperText?: string;
    marginBottom?: number;
    hidden?: boolean;
    showCharacterCount?: boolean;     // NEW
    showFileCount?: boolean;          // NEW
  };
}

const Component = ({
  name, value, disabled, showToolbar = true, placeholder,
  error, touched, onFocus, onBlur, onChange, entityType, options = {}, user,
  onUploadCountChange, max, maxFiles = 5, customToolbarItems = [], mediaFilesMap, blockFilesDownload = true
}: TextareaProps) => {
  const [field, meta, helpers] = useField({ name, value });
  const { classes, setActive } = useFormikFieldValidation(error, name, disabled);
  const targetDivRef = useRef<HTMLDivElement>(null);
  const [characterCount, setCharacterCount] = useState(0);
  const [textCharacterCount, setTextCharacterCount] = useState(0);
  const [mediaBreakdown, setMediaBreakdown] = useState({ images: 0, videos: 0, pdfs: 0, audios: 0 });
  const [fileCount, setFileCount] = useState(0);
  const [initialLinkData, setInitialLinkData] = useState<{ text: string; href: string }>({ text: "", href: "" });
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showEditLinkModal, setShowEditLinkModal] = useState(false);
  const [editingLinkData, setEditingLinkData] = useState<{ text: string; href: string; nodePos?: number }>({ text: "", href: "" });

  const parsedInitialContent = useMemo(() => {
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (parsed?.jsonStr) {
          return JSON.parse(parsed.jsonStr); // viene de un post anterior
        }
        if (parsed?.type === "doc") {
          return parsed; // ya es un documento válido
        }
      } catch (e) {
      }
    }
    return { type: "doc", content: [{ type: "paragraph", content: [] }] }; // fallback with valid paragraph
  }, [value]);



  // Callback para editar links desde el popover
  const handleEditLink = useCallback((text: string, href: string) => {
    setEditingLinkData({ text, href });
    setShowEditLinkModal(true);
  }, []);

  const visual = useRemirror<any>({
    extensions: () => createExtensions(mediaFilesMap, handleEditLink, blockFilesDownload, placeholder),
    content: parsedInitialContent,
  });

  useEffect(() => {
    const editorView = visual?.getContext()?.view;
    if (!editorView) return;

    // Handle keyboard shortcuts - prevent Ctrl+Z crashes by validating before allowing ProseMirror to handle
    // Use document-level listener with capture to ensure we intercept before ProseMirror
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if the editor is focused
      if (!editorView.hasFocus()) return;

      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        try {
          const { state } = editorView;

          // Validate document state before allowing undo/redo
          if (!state.doc || !state.doc.content) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return;
          }

          // Check if document content is valid
          try {
            state.doc.check();
          } catch (docError) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            return;
          }

          // If we get here, the document is valid - allow the operation

        } catch (error) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      }
    };

    // Use document-level event listener with capture to intercept before ProseMirror
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [visual]);


  const markdown = useRemirror<any>({
    extensions: () => [
      new DocExtension({ content: "codeBlock" }),
      new CodeBlockExtension({ defaultLanguage: "markdown" }),
    ],
    stringHandler: "markdown",
  });

  // Character count display component
  const CharacterCountDisplay = () => {
    if (!options.showCharacterCount && !max && !options.showFileCount) return null;

    const isAtLimit = max && characterCount === max;
    const isExceeded = max && characterCount > max;

    const isFileCountExceeded = fileCount > maxFiles;
    const isFileCountAtLimit = fileCount === maxFiles;

    return (
      <CharacterCounterWrapper>
        <div>
          {(options.showCharacterCount || options.showFileCount) && (
            <span style={{ color: "#666" }}>Limites: </span>
          )}
          {(options.showCharacterCount || max) && (
            <span style={{ color: isExceeded ? '#d32f2f' : isAtLimit ? '#ff9800' : '#666' }}>
              {max ? `${characterCount.toLocaleString('es-AR')}/${max.toLocaleString('es-AR')} caracteres` : `${characterCount.toLocaleString('es-AR')} caracteres`}
            </span>
          )}
          {options.showFileCount && (
            <span style={{ color: isFileCountExceeded ? '#d32f2f' : isFileCountAtLimit ? '#ff9800' : '#666', marginLeft: (options.showCharacterCount || max) ? '12px' : '0' }}>
              {typeof fileCount !== 'undefined' ? fileCount : 0}/{maxFiles} archivos multimedia
            </span>
          )}
        </div>
      </CharacterCounterWrapper>
    );
  };

  return (
    <>
      <AllStyledComponent>
        <ThemeProvider>
          <DualEditorProvider visual={visual} markdown={markdown}>
            <FieldContainer marginBottom={options.marginBottom}>
              <div ref={targetDivRef} />
              {options.label && (
                <Label size={options.size} htmlFor={`textarea-${name}`}>
                  {options.label}
                </Label>
              )}
              <Field
                size={options.size}
                skin={options.skin}
                className={classes}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: 0,
                  position: "relative",
                  zIndex: 1
                }}
              >
                <RichTextEditorStyles height={options.height} />
                <CustomVideoExtensionStyles />
                <CustomImageExtensionStyles />
                <CustomPdfExtensionStyles />
                <CustomAudioExtensionStyles />
                <CustomMediaExtensionStyles />
                <VisualEditor
                  name={name}
                  max={max}
                  maxFiles={maxFiles}
                  entityType={entityType}
                  userId={user?.id}
                  disabled={disabled}
                  showToolbar={showToolbar}
                  onBlur={onBlur}
                  customToolbarItems={customToolbarItems}
                  onFocus={onFocus}
                  onUploadCountChange={onUploadCountChange}
                  blockFilesDownload={blockFilesDownload}
                  onChange={({
                    markdownStr,
                    json,
                    textStr,
                    htmlStr,
                    characterCount: totalCount,
                    textCharacterCount: textCount,
                    mediaBreakdown: breakdown,
                    wordsCount,
                    isCountValid,
                    fileCount: currentFileCount,
                    isFileCountValid
                  }) => {
                    setCharacterCount(totalCount);
                    setTextCharacterCount(textCount);
                    setMediaBreakdown(breakdown);
                    setFileCount(currentFileCount);

                    onChange?.({
                      markdownStr,
                      json,
                      textStr,
                      htmlStr,
                      characterCount: totalCount,
                      textCharacterCount: textCount,
                      mediaBreakdown: breakdown,
                      wordsCount,
                      isCountValid,
                      fileCount: currentFileCount,
                      isFileCountValid
                    });

                    // Validar límites y setear errores
                    const errors: string[] = [];

                    if (max && totalCount > max) {
                      errors.push(`Límite de caracteres excedido`);
                    }

                    if (currentFileCount > maxFiles) {
                      errors.push(`Límite archivos excedido`);
                    }

                    if (errors.length > 0) {
                      helpers.setError(errors.join('. '));
                    } else {
                      helpers.setError(undefined);
                    }

                    const newValue = JSON.stringify({ jsonStr: JSON.stringify(json), htmlStr, textStr });
                    helpers.setValue(newValue);
                  }}
                />
                <CharacterCountDisplay />
              </Field>

              {((options.helperText) || error) && (
                <HelperLine marginBottom={options.marginBottom}>
                  {error && touched && <ErrorMessage>{String(error)}</ErrorMessage>}
                  {options.helperText && !error && <HelperText>{options.helperText}</HelperText>}
                </HelperLine>
              )}
            </FieldContainer>
          </DualEditorProvider>
        </ThemeProvider>
      </AllStyledComponent>



      {showLinkModal && (
        <InsertLinkModal
          initialText={initialLinkData.text}
          initialUrl={initialLinkData.href}
          onInsert={({ text, url }) => {
            const view = visual.getContext()?.view;
            if (!view) return;

            const { state, dispatch } = view;
            const { from, to } = state.selection;
            const mark = state.schema.marks.link.create({ href: url, target: "_blank" });
            const node = state.schema.text(text, [mark]);
            const tr = state.tr.replaceWith(from, to, node);
            dispatch(tr.scrollIntoView());
          }}
          onClose={() => setShowLinkModal(false)}
        />
      )}

      {showEditLinkModal && (
        <InsertLinkModal
          isEditing={true}
          initialText={editingLinkData.text}
          initialUrl={editingLinkData.href}
          onInsert={({ text, url }) => {
            const view = visual.getContext()?.view;
            if (!view) return;

            // Buscar y actualizar el nodo customLink que se está editando
            const { state, dispatch } = view;
            const doc = state.doc;

            doc.descendants((node, pos) => {
              if (node.type.name === 'customLink' &&
                node.attrs.href === editingLinkData.href &&
                node.attrs.text === editingLinkData.text) {

                const updatedNode = node.type.create({
                  ...node.attrs,
                  href: url,
                  text: text
                });

                const tr = state.tr.replaceWith(pos, pos + node.nodeSize, updatedNode);
                dispatch(tr.scrollIntoView());
                return false; // Detener la búsqueda
              }
              return true;
            });
          }}
          onClose={() => {
            setShowEditLinkModal(false);
            setEditingLinkData({ text: "", href: "" });
          }}
        />
      )}
    </>
  );
};


const state = ({ userStore }) => {
  const { data: userInformation } = userStore.information;

  return {
    user: userInformation
  };
};

export default connect(state)(Component);
